import type { ToastTone } from "@app/ui";
import { create } from "zustand";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
  /** Optional button (e.g. "Undo"). Dismisses the toast after running. */
  action?: ToastAction | undefined;
  /** ms before auto-dismiss; 0 keeps it until manually dismissed. */
  duration: number;
}

interface ToastState {
  toasts: ToastItem[];
  push: (tone: ToastTone, message: string, duration?: number, action?: ToastAction) => number;
  dismiss: (id: number) => void;
}

let nextId = 1;
const timers = new Map<number, ReturnType<typeof setTimeout>>();
const DEFAULTS: Record<ToastTone, number> = { success: 4000, info: 4000, error: 6000 };

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  push: (tone, message, duration, action) => {
    const id = nextId++;
    const ms = duration ?? DEFAULTS[tone];
    const wrapped: ToastAction | undefined = action
      ? { label: action.label, onClick: () => { action.onClick(); get().dismiss(id); } }
      : undefined;
    set((s) => ({ toasts: [...s.toasts, { id, tone, message, action: wrapped, duration: ms }] }));
    if (ms > 0) timers.set(id, setTimeout(() => get().dismiss(id), ms));
    return id;
  },
  dismiss: (id) => {
    const t = timers.get(id);
    if (t) {
      clearTimeout(t);
      timers.delete(id);
    }
    set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) }));
  },
}));

/** Imperative API usable anywhere (stores, async handlers), not just React. */
export const toast = {
  success: (message: string, duration?: number) =>
    useToastStore.getState().push("success", message, duration),
  error: (message: string, duration?: number) =>
    useToastStore.getState().push("error", message, duration),
  info: (message: string, duration?: number) =>
    useToastStore.getState().push("info", message, duration),
  /** A toast with an action button (e.g. "Undo"). The button runs `action.onClick`
   *  then dismisses the toast. */
  withAction: (tone: ToastTone, message: string, action: ToastAction, duration?: number) =>
    useToastStore.getState().push(tone, message, duration, action),
};
